import React, {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { produce } from 'immer';
import toast from 'react-hot-toast';
import { Calculator, PlusCircle } from 'lucide-react';

import OperationRow from './OperationRow';
import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import { OPERATIONS } from '../NewWorkflow/utils/operations';
import {
  schemaToFields,
  fieldsToSchema,
} from '../NewWorkflow/utils/schemaUtils';

// ⭐ THE FIX: This function now correctly generates paths that match the SchemaViewer.
const flattenSchemaToFields = (rootFields) => {
  const fields = [];
  const recurse = (nodes, pathPrefix, displayPrefix) => {
    for (const node of nodes) {
      // Generate paths and display names exactly as SchemaViewer does.
      const newPath = pathPrefix ? `${pathPrefix}.${node.fieldName}` : node.id;
      const newDisplay = displayPrefix
        ? `${displayPrefix}.${node.fieldName}`
        : node.fieldName;

      fields.push({ ...node, path: newPath, display: newDisplay });

      if (node.children && node.children.length > 0) {
        recurse(node.children, newPath, newDisplay);
      }
    }
  };
  recurse(rootFields, '', '');
  return fields;
};

// // Helper to convert schema to fields (from previous nodes)
// const schemaToFields = (schema, prefix) => {
//     if (!schema || !schema.properties) return [];
//     return Object.entries(schema.properties).map(([key, value]) => ({
//         id: `${prefix}${key}`,
//         fieldName: key,
//         dataType: value.type || 'string',
//         children: (value.type === 'object' && value.properties) ? schemaToFields(value, `${prefix}${key}.`) : [],
//         arrayItemType: value.items?.type,
//     }));
// };

const OperationsNodeModal = ({
  mode,
  node,
  nodes,
  edges,
  onSave,
  onClose,
  onDelete,
  globalState,
}) => {
  const [operations, setOperations] = useState(node.data.operations || []);
  const [selectedOperandTarget, setSelectedOperandTarget] = useState(null);

  const containerRef = useRef(null);
  const elementRefs = useRef(new Map());
  const [lines, setLines] = useState([]);

  // const inputRootFields = useMemo(() => {
  //     return edges
  //         .filter(edge => edge.target === node.id)
  //         .map(edge => nodes.find(n => n.id === edge.source))
  //         .filter(Boolean)
  //         .map(parentNode => {
  //             const schema = parentNode?.data?.outputSchema;
  //             if (!schema) return null;
  //             return {
  //                 id: `input_${parentNode.id}`,
  //                 fieldName: parentNode.data.label || 'Input Node',
  //                 dataType: 'Object',
  //                 children: schemaToFields(schema, ''),
  //             };
  //         }).filter(Boolean);
  // }, [node.id, nodes, edges]);

  const inputRootFields = useMemo(() => {
    // return edges
    //     .filter(edge => edge.target === node.id)
    //     .map(edge => nodes.find(n => n.id === edge.source))
    //     .filter(Boolean)
    //     .map(parentNode => {
    //         let schema;
    //         // --- THIS IS THE CRITICAL LOGIC ---
    //         // Check if the parent has a defined JSON output.
    //         if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
    //             schema = parentNode.data.jsonOutputSchema;
    //         } else {
    //             // If not, create a default schema for its simple string output.
    //             schema = {
    //                 title: 'Simple Output',
    //                 type: 'object',
    //                 properties: {
    //                     'output': {
    //                         type: 'string',
    //                         description: `The plain text output from "${parentNode.data.label || parentNode.type}"`
    //                     }
    //                 }
    //             };
    //         }
    //         // --- END OF CRITICAL LOGIC ---

    //         if (!schema) return null;

    //         const inputKey = `input_${parentNode.id}`;
    //         return {
    //             id: inputKey,
    //             fieldName: parentNode.data.label || 'Input Node',
    //             dataType: 'Object',
    //             children: schemaToFields(schema, ''),
    //         };
    //     }).filter(Boolean);

    const connectedInputNodes = edges
      .filter((edge) => edge.target === node.id)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean)
      .filter((parentNode) => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
      .map((parentNode) => {
        let schema;
        if (
          parentNode.data.jsonOutput &&
          parentNode.data.jsonOutputSchema?.properties
        ) {
          schema = parentNode.data.jsonOutputSchema;
        } else {
          schema = {
            title: 'Simple Output',
            type: 'object',
            properties: {
              output: {
                type: 'string',
                description: `Output from "${parentNode.data.label || parentNode.type}"`,
              },
            },
          };
        }
        return {
          id: `input_${parentNode.id}`,
          fieldName: parentNode.data.label || 'Input Node',
          dataType: 'Object',
          children: schemaToFields(schema, ''),
        };
      })
      .filter(Boolean);

    // ⭐ 2. Create a "virtual node" for the Global State if it exists
    const globalStateNode = [];
    if (globalState && globalState.fields.length > 0) {
      const globalSchema = fieldsToSchema(globalState.fields, 'globalState');
      globalStateNode.push({
        id: 'globalState', // Special, reserved ID
        fieldName: 'Global State',
        dataType: 'Object',
        children: schemaToFields(globalSchema, ''),
      });
    }

    // ⭐ 3. Combine the Global State with the other input nodes
    return [...globalStateNode, ...connectedInputNodes];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, nodes, edges]);

  const allInputFields = useMemo(
    () => flattenSchemaToFields(inputRootFields),
    [inputRootFields],
  );

  const registerRef = useCallback((id, el) => {
    if (el) elementRefs.current.set(id, el);
    else elementRefs.current.delete(id);
  }, []);

  // Logic to draw SVG lines between mapped fields and slots
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const newLines = [];
    const cRect = container.getBoundingClientRect();

    operations.forEach((op) => {
      // Line for Operand 1
      if (op.operand1Path) {
        const startEl = elementRefs.current.get(`field-${op.operand1Path}`);
        const endEl = elementRefs.current.get(`operand-${op.id}-1`);
        if (startEl && endEl) {
          const sRect = startEl.getBoundingClientRect();
          const eRect = endEl.getBoundingClientRect();
          const d = `M ${sRect.right - cRect.left} ${sRect.top + sRect.height / 2 - cRect.top} C ${sRect.right - cRect.left + 60} ${sRect.top + sRect.height / 2 - cRect.top}, ${eRect.left - cRect.left - 60} ${eRect.top + eRect.height / 2 - cRect.top}, ${eRect.left - cRect.left} ${eRect.top + eRect.height / 2 - cRect.top}`;
          newLines.push({ id: `${op.id}-1`, d });
        }
      }
      // Line for Operand 2
      if (op.operand2Path) {
        const startEl = elementRefs.current.get(`field-${op.operand2Path}`);
        const endEl = elementRefs.current.get(`operand-${op.id}-2`);
        if (startEl && endEl) {
          const sRect = startEl.getBoundingClientRect();
          const eRect = endEl.getBoundingClientRect();
          const d = `M ${sRect.right - cRect.left} ${sRect.top + sRect.height / 2 - cRect.top} C ${sRect.right - cRect.left + 60} ${sRect.top + sRect.height / 2 - cRect.top}, ${eRect.left - cRect.left - 60} ${eRect.top + eRect.height / 2 - cRect.top}, ${eRect.left - cRect.left} ${eRect.top + eRect.height / 2 - cRect.top}`;
          newLines.push({ id: `${op.id}-2`, d });
        }
      }
    });
    setLines(newLines);
  }, [operations, allInputFields]); // Rerun when operations or fields change

  const handleAddOperation = () => {
    setOperations(
      produce((draft) => {
        draft.push({ id: `op_${Date.now()}` });
      }),
    );
  };

  const handleUpdateOperation = (index, updatedOp) => {
    setOperations(
      produce((draft) => {
        draft[index] = updatedOp;
      }),
    );
  };

  const handleDeleteOperation = (index) => {
    setOperations(
      produce((draft) => {
        draft.splice(index, 1);
      }),
    );
  };

  const handleSelectOperand = (opId, operandKey) => {
    setSelectedOperandTarget({ opId, operandKey });
  };

  // const handleFieldClick = (path, field) => {
  //     if (!selectedOperandTarget) {
  //         toast.error("Please select an operand slot first.");
  //         return;
  //     }

  //     const { opId, operandKey } = selectedOperandTarget;
  //     const opIndex = operations.findIndex(op => op.id === opId);
  //     if (opIndex === -1) return;

  //     const currentOp = operations[opIndex];

  //     if (operandKey === 'operand2Path') {
  //         const op1Field = allInputFields.find(f => f.path === currentOp.operand1Path);
  //         const operator = OPERATIONS[currentOp.operator];
  //         const requiredTypes = operator.getOperand2Types(op1Field);
  //         if (!requiredTypes.includes(field.dataType.toLowerCase())) {
  //             toast.error(`Invalid type. Expected ${requiredTypes.join(' or ')}.`);
  //             return;
  //         }
  //     }

  //     const updatedOp = produce(currentOp, draft => {
  //         draft[operandKey] = path;
  //         if (operandKey === 'operand1Path') {
  //             const operatorInfo = OPERATIONS[draft.operator];
  //             if (operatorInfo && !operatorInfo.appliesTo.includes(field.dataType.toLowerCase())) {
  //                 draft.operator = null;
  //                 draft.operand2Path = null;
  //             }
  //         }
  //     });

  //     handleUpdateOperation(opIndex, updatedOp);
  //     setSelectedOperandTarget(null);
  // };

  const handleFieldClick = (path, field) => {
    if (!selectedOperandTarget) {
      toast.error('Please select an operand slot first.');
      return;
    }

    const { opId, operandKey } = selectedOperandTarget;
    const opIndex = operations.findIndex((op) => op.id === opId);
    if (opIndex === -1) return;

    const currentOp = operations[opIndex];

    // --- VALIDATION LOGIC (from your original function) ---
    // This part is crucial and was missing from my previous suggestion.
    if (operandKey === 'operand2Path') {
      const op1Field = allInputFields.find(
        (f) => f.path === currentOp.operand1Path,
      );
      const operator = OPERATIONS[currentOp.operator];

      // Ensure we can actually perform validation
      if (op1Field && operator) {
        const requiredTypes = operator.getOperand2Types(op1Field);
        if (!requiredTypes.includes(field.dataType.toLowerCase())) {
          toast.error(`Invalid type. Expected ${requiredTypes.join(' or ')}.`);
          return; // Stop the function here
        }
      } else {
        toast.error(
          'Please set Operand 1 and an Operator before choosing Operand 2.',
        );
        return;
      }
    }

    // --- UPDATE LOGIC (combining both versions) ---
    const updatedOp = produce(currentOp, (draft) => {
      // Set the path for the selected operand
      draft[operandKey] = path;

      // NEW: If we're setting Operand 2, also record its data type for the backend
      if (operandKey === 'operand2Path') {
        draft.operand2DataType = field.dataType;
      }

      // If we change Operand 1, reset the operator (if invalid) and clear Operand 2
      if (operandKey === 'operand1Path') {
        draft.operand1DataType = field.dataType; // ⭐ ADD THIS LINE

        // Clear operand 2 since it may no longer be valid
        draft.operand2Path = null;
        draft.operand2DataType = null; // Also clear its type

        // Check if the current operator is still valid for the new Operand 1 type
        const operatorInfo = OPERATIONS[draft.operator];
        if (
          operatorInfo &&
          !operatorInfo.appliesTo.includes(field.dataType.toLowerCase())
        ) {
          draft.operator = null; // Reset operator if it's not compatible
        }
      }
    });

    handleUpdateOperation(opIndex, updatedOp);
    setSelectedOperandTarget(null);
  };

  const isFieldValid = (field) => {
    if (!selectedOperandTarget) return true;

    const { opId, operandKey } = selectedOperandTarget;
    const op = operations.find((o) => o.id === opId);

    if (!op) return false;

    if (operandKey === 'operand1Path') {
      return true;
    }

    if (operandKey === 'operand2Path') {
      const op1Field = allInputFields.find((f) => f.path === op.operand1Path);
      const operator = OPERATIONS[op.operator];
      if (!op1Field || !operator) return false;

      // ⭐ UPDATED: Add specific validation for array item types
      if (op.operator === 'concatenateArrays') {
        return (
          field.dataType.toLowerCase() === 'array' &&
          field.arrayItemType === op1Field.arrayItemType
        );
      }

      const requiredTypes = operator.getOperand2Types(op1Field);
      return requiredTypes.includes(field.dataType.toLowerCase());
    }
    return true;
  };

  // const handleSaveAndClose = () => {
  //     const properties = {};
  //     const required = [];
  //     const outputFields = [];

  //     operations.forEach(op => {
  //         if (!op.outputFieldName || !op.operand1Path || !op.operator) return;

  //         const selectedOp1 = allInputFields.find(f => f.path === op.operand1Path);
  //         const opInfo = OPERATIONS[op.operator];

  //         if (selectedOp1 && opInfo) {
  //             // ⭐ UPDATED: Use the new detailed type object from getOutputType
  //             const outputTypeInfo = opInfo.getOutputType(selectedOp1);

  //             // Build the schema property
  //             const schemaProperty = { type: outputTypeInfo.dataType };
  //             if (outputTypeInfo.dataType === 'array') {
  //                 // Correctly handle the items schema for arrays
  //                 schemaProperty.items = { type: (outputTypeInfo.arrayItemType || 'string').toLowerCase() };
  //             }
  //             properties[op.outputFieldName] = schemaProperty;
  //             required.push(op.outputFieldName);

  //             // Build the outputFields object with complete information
  //             outputFields.push({
  //                 id: `op_field_${op.id}`,
  //                 fieldName: op.outputFieldName,
  //                 dataType: outputTypeInfo.dataType.charAt(0).toUpperCase() + outputTypeInfo.dataType.slice(1),
  //                 description: `Output of '${opInfo.label}' operation.`,
  //                 children: [],
  //                 // Correctly deduce the arrayItemType
  //                 arrayItemType: outputTypeInfo.arrayItemType || 'String',
  //             });
  //         }
  //     });

  //     const finalOutputSchema = {
  //         title: "OperationsOutput",
  //         type: "object",
  //         properties,
  //         required: required.length > 0 ? required : undefined,
  //     };

  //     onSave({
  //         ...node,
  //         data: {
  //             ...node.data,
  //             label: `Operations (${operations.length} defined)`,
  //             operations,
  //             outputFields,
  //             jsonOutputSchema: finalOutputSchema,
  //             jsonOutput: true, // <-- THIS IS THE CRITICAL MISSING PIECE
  //         },
  //     });
  //     onClose();
  // };

  // const handleSaveAndClose = () => {
  //     const outputFields = [];

  //     operations.forEach(op => {
  //         if (!op.outputFieldName || !op.operand1Path || !op.operator) return;

  //         const selectedOp1 = allInputFields.find(f => f.path === op.operand1Path);
  //         const opInfo = OPERATIONS[op.operator];

  //         if (selectedOp1 && opInfo) {
  //             const outputTypeInfo = opInfo.getOutputType(selectedOp1);

  //             // ⭐ THE FIX: Build a detailed field object, preserving nested structures.
  //             const newField = {
  //                 id: `op_field_${op.id}`,
  //                 fieldName: op.outputFieldName,
  //                 dataType: outputTypeInfo.dataType.charAt(0).toUpperCase() + outputTypeInfo.dataType.slice(1),
  //                 description: `Output of '${opInfo.label}' operation.`,
  //                 children: [],
  //                 arrayItemType: 'String'
  //             };

  //             // If the output is a complex type, copy the structure from Operand 1.
  //             // This assumes the operation preserves the internal structure.
  //             if (outputTypeInfo.dataType === 'object' || outputTypeInfo.dataType === 'array') {
  //                 newField.children = selectedOp1.children || [];
  //                 newField.arrayItemType = selectedOp1.arrayItemType || 'String';
  //             }

  //             outputFields.push(newField);
  //         }
  //     });

  //     // ⭐ THE FIX: Use the 'fieldsToSchema' helper to generate the schema from our detailed 'outputFields' array.
  //     const finalOutputSchema = fieldsToSchema(outputFields, "OperationsOutput");

  //     onSave({
  //         ...node,
  //         data: {
  //             ...node.data,
  //             label: `Operations (${operations.length} defined)`,
  //             operations,
  //             outputFields, // Now correctly saved with full structure
  //             jsonOutputSchema: finalOutputSchema, // Now correctly generated with full structure
  //             jsonOutput: true, // <-- THIS IS THE CRITICAL MISSING PIECE
  //         },
  //     });
  //     onClose();
  // };

  const handleSaveAndClose = () => {
    const outputFields = [];

    operations.forEach((op) => {
      if (!op.outputFieldName || !op.operand1Path || !op.operator) return;

      const selectedOp1 = allInputFields.find(
        (f) => f.path === op.operand1Path,
      );
      const opInfo = OPERATIONS[op.operator];

      if (selectedOp1 && opInfo) {
        // --- THIS IS THE CORRECTED LOGIC ---
        // 1. Get the definitive output schema from operations.js
        const outputSchemaInfo = opInfo.getOutputSchema(selectedOp1);

        // 2. Build the new field object based on that schema
        const newField = {
          id: `op_field_${op.id}`,
          fieldName: op.outputFieldName,
          dataType: outputSchemaInfo.dataType, // e.g., 'Number' for length, 'Boolean' for equals
          description: `Output of '${opInfo.label}' operation.`,
          // Pass through children and arrayItemType if the schema provides them
          children: outputSchemaInfo.children || [],
          arrayItemType: outputSchemaInfo.arrayItemType || 'String',
        };

        outputFields.push(newField);
      }
    });

    // Use the standard helper to generate the final schema from the correct outputFields
    const finalOutputSchema = fieldsToSchema(outputFields, 'OperationsOutput');

    onSave({
      ...node,
      data: {
        ...node.data,
        label: `Operations (${operations.length} defined)`,
        operations,
        outputFields, // This is now correctly generated
        jsonOutputSchema: finalOutputSchema, // This is now correctly generated
        jsonOutput: true,
      },
    });
    onClose();
  };

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="flex h-[90vh] w-full max-w-7xl flex-col rounded-xl border border-slate-700 bg-slate-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-700 p-4">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-white">
            <Calculator /> Configure Operations
          </h2>
          <button
            onClick={handleAddOperation}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <PlusCircle size={16} /> Add Operation
          </button>
        </div>

        <div
          className="relative grid flex-grow grid-cols-12 gap-4 overflow-hidden p-4"
          ref={containerRef}
        >
          <svg className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full">
            {lines.map((line) => (
              <path
                key={line.id}
                d={line.d}
                stroke="#f97316"
                strokeWidth="2"
                fill="none"
                className="opacity-75"
              />
            ))}
          </svg>

          <div className="col-span-5 flex flex-col gap-2 overflow-y-auto pr-2">
            <h3 className="text-lg font-semibold text-slate-200">
              Available Inputs
            </h3>
            <SchemaViewer
              rootFields={inputRootFields}
              onFieldClick={handleFieldClick}
              isValidField={isFieldValid}
              registerRef={(id, el) => registerRef(`field-${id}`, el)}
            />
          </div>

          <div className="col-span-7 flex flex-col gap-2 overflow-y-auto pl-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-200">
                Defined Operations
              </h3>
            </div>
            <div className="space-y-4">
              {operations.length === 0 && (
                <div className="py-16 text-center text-slate-500">
                  No operations defined. Click "Add Operation" to get started.
                </div>
              )}
              {operations.map((op, index) => (
                <OperationRow
                  key={op.id}
                  operation={op}
                  allInputFields={allInputFields}
                  onUpdate={(updatedOp) =>
                    handleUpdateOperation(index, updatedOp)
                  }
                  onDelete={() => handleDeleteOperation(index)}
                  onSelectOperand={(operandKey) =>
                    handleSelectOperand(op.id, operandKey)
                  }
                  selectedOperandTarget={
                    selectedOperandTarget?.opId === op.id
                      ? selectedOperandTarget
                      : null
                  }
                  registerRef={registerRef}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center justify-between rounded-b-lg border-t border-zinc-700 bg-zinc-800/50 p-4">
          {mode === 'edit' && node?.id && (
            <button
              onClick={() => onDelete(node.id)}
              className="rounded-md bg-red-900/50 px-4 py-2 text-sm font-semibold text-red-400 transition-colors hover:bg-red-900/80"
            >
              Delete Node
            </button>
          )}
          <div className="flex-grow" />
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-zinc-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndClose}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsNodeModal;
